import { createContext, useEffect, useState, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, Route, Routes } from "react-router-dom";

export const ReactLazyPreload = importStatement => {
    const Component = lazy(importStatement);
    Component.preload = importStatement;
    return Component;
};

const Home = ReactLazyPreload(() => import('../Pages/Home'));
const Content = ReactLazyPreload(() => import('../Pages/Content'));
const Property = ReactLazyPreload(() => import('../Pages/Property'));
const Properties = ReactLazyPreload(() => import('../Pages/Properties'));
const News = ReactLazyPreload(() => import('../Pages/News'));
const Article = ReactLazyPreload(() => import('../Pages/Article'));
const PQR = ReactLazyPreload(() => import('../Pages/PQR'));
const Repair = ReactLazyPreload(() => import('../Pages/Repair'));
const Postulate = ReactLazyPreload(() => import('../Pages/Postulate'));
const Contracts = ReactLazyPreload(() => import('../Pages/Contracts'));
const Login = ReactLazyPreload(() => import('../Pages/Login'));
const Favorite = ReactLazyPreload(() => import('../Pages/Favorite'));
const NotFound = ReactLazyPreload(() => import('../Pages/NotFound'));

export const LocaleContext = createContext({
    locale: '',
    setLocale: (newLocale) => { },
});

function ScrollToTop() {
    const { pathname } = useLocation();

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo(0, 0);
        })
    }, [pathname]);

    return null;
}

export const LangRouter = () => {
    const { t, i18n } = useTranslation();
    const { pathname, search, hash } = useLocation();
    const navigate = useNavigate();
    const defaulLocale = "es";
    const avaliableLocales = ['en', 'es'];
    const segments = pathname.split('/').filter(Boolean);
    const pathnameLocale = avaliableLocales.includes(segments[0]) ? segments[0] : defaulLocale;
    const [locale, setLocale] = useState(defaulLocale);

    useEffect(() => {
        if (avaliableLocales.includes(pathnameLocale)) {
            setLocale(pathnameLocale);
            setLanguageHandler(pathnameLocale);
        } else {
            setLocale(defaulLocale);
            setLanguageHandler(defaulLocale);
        }

    }, [pathname, pathnameLocale]);

    const setLanguageHandler = (lang) => {
        i18n.changeLanguage(lang);
    };

    const updateLocale = (newLocale) => {
        const segments = pathname.split('/').filter(Boolean);
        const hasLocale = avaliableLocales.includes(segments[0]);

        let newPathSegments = [...segments];

        if (hasLocale) {
            newPathSegments.splice(0, 1);
        }

        const basePath = "/" + newPathSegments.join('/');

        const finalPath =
            newLocale === defaulLocale
                ? basePath
                : `/${newLocale}${basePath}`;

        navigate(finalPath + hash + search);
        setLocale(newLocale);
        setLanguageHandler(newLocale);
    };

    const langPrefix = locale === defaulLocale ? "" : `/${locale}`;
    return (
        <LocaleContext.Provider value={{ locale, setLocale: updateLocale }}>
            <ScrollToTop />
            <Suspense fallback={<div />}>
                <Routes>
                    <Route
                        path={langPrefix || "/"}
                        element={<Home />}
                    />
                    <Route
                        path={`${langPrefix}/${t('url.data-policy', { lng: locale })}`}
                        element={<Content url={t('url.data-policy')} title={t('data-policy')} keyVar={t('vars.data-policy')} />}
                    />
                    <Route
                        path={`${langPrefix}/${t('url.work-with-us', { lng: locale })}`}
                        element={<Content url={t('url.work-with-us')} title={t('work-with-us')} keyVar={t('vars.work-with-us')} />}
                    />
                    <Route
                        path={`${langPrefix}/${t('url.how-to-rent', { lng: locale })}`}
                        element={<Content url={t('url.how-to-rent')} title={t('how-to-rent')} keyVar={t('vars.how-to-rent')} />}
                    />
                    <Route
                        path={`${langPrefix}/${t('url.about-us', { lng: locale })}`}
                        element={<Content url={t('url.about-us')} title={t('about-us')} keyVar={t('vars.about-us')} />}
                    />
                    <Route
                        path={`${langPrefix}/${t('url.tenants', { lng: locale })}`}
                        element={<Content url={t('url.tenants')} title={t('tenants')} keyVar={t('vars.tenants')} mainClass={"_301_fm_call"} />}
                    />
                    <Route
                        path={`${langPrefix}/${t('url.owners', { lng: locale })}`}
                        element={<Content url={t('url.owners')} title={t('owners')} keyVar={t('vars.owners')} mainClass={"_301_fm_call"} />}
                    />
                    <Route
                        path={`${langPrefix}/${t('url.favorites', { lng: locale })}/:uuid?`}
                        element={<Favorite url={t('url.favorites')} title={t('favorites')} />}
                    />
                    <Route
                        path={`${langPrefix}/${t('url.advisors', { lng: locale })}`}
                        element={<Login />}
                    />
                    <Route
                        path={`${langPrefix}/${t('url.properties', { lng: locale })}`}
                        element={<Properties />}
                    />
                    <Route
                        path={`/inmuebles/:code`}
                        element={<Property />}
                    />
                    <Route
                        path={`/en/properties/:code`}
                        element={<Property />}
                    />
                    <Route
                        path={`${langPrefix}/${t('url.news', { lng: locale })}`}
                        element={<News />}
                    />
                    <Route
                        path={`${langPrefix}/${t('url.news', { lng: locale })}/:slug`}
                        element={<Article />}
                    />
                    <Route
                        path={`${langPrefix}/${t('url.pqr', { lng: locale })}`}
                        element={<PQR mainClass="_301_fm_call" />}
                    />
                    <Route
                        path={`${langPrefix}/${t('url.repair', { lng: locale })}`}
                        element={<Repair mainClass="_301_fm_call" />}
                    />
                    <Route
                        path={`${langPrefix}/${t('url.postulate', { lng: locale })}`}
                        element={<Postulate mainClass="_301_fm_call" />}
                    />
                    <Route
                        path={`${langPrefix}/${t('url.contracts', { lng: locale })}`}
                        element={<Contracts mainClass="_301_fm_call" />}
                    />

                    <Route path="*"
                        element={<NotFound mainClass="_301_fm_call" />}
                    />
                </Routes>
            </Suspense>
        </LocaleContext.Provider>
    );
}